import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TextField } from '@nextiva/next-ui-components';
import { debounce } from 'lodash';
import clsx from 'clsx';

export interface AddressComponents {
  street_number?: string;
  route: string;
  neighborhood: string;
  political: string;
  locality: string;
  administrative_area_level_2: string;
  administrative_area_level_1: string;
  country: string;
  postal_code: string;
  postal_code_suffix: string;
}

const autocompleteService = { current: null };
const placesService = { current: null };
const dataTestId = 'PlatformAdminUI-BrandRegistration-GoogleAutoCompleteInput';

export const GoogleAutoCompleteInput = ({
  clearAllAddressFields,
  clearCityStateZipFields,
  handleAddressChange,
  onInputChange,
  label = '',
  value = '',
  error = false,
  disabled = false,
  isModal = false,
}: {
  onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearAllAddressFields: () => void;
  clearCityStateZipFields: () => void;
  handleAddressChange: (
    addressComponents: AddressComponents,
    streetNumber?: string,
  ) => void;
  label?: React.ReactNode;
  value?: string;
  error?: boolean;
  disabled?: boolean;
  isModal?: boolean;
}) => {
  const [didSelectAddress, setDidSelectAddress] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const [inputValue, setInputValue] = useState(value);
  const addressRef = useRef(null);

  // we need to pass the streetNumber synchronously to handleAddressChange so we initialize a variable for later
  let streetNumber: string;

  const placeServiceCallback = (results, status) => {
    if (status === window.google.maps.places.PlacesServiceStatus.OK) {
      const place = results.length && results[0];

      const newSuggestion = {
        description: place?.formatted_address,
        place_id: place?.place_id,
      };
      const checkPlaceId = suggestions.some(
        (suggestion) => suggestion?.place_id === place?.place_id,
      );

      if (!checkPlaceId) setSuggestions([...suggestions, newSuggestion]);
    }
  };

  const fetchSuggestionsCallback = (results) => {
    setSuggestions(results || []);

    if (!results) {
      const map = new window.google.maps.Map(addressRef.current);
      const request = {
        query: addressRef.current?.value,
      };

      const service = new window.google.maps.places.PlacesService(map);

      service.textSearch(request, placeServiceCallback);
    }
  };

  const fetchSuggestions = debounce((input) => {
    autocompleteService.current.getPlacePredictions(
      {
        input,
        types: ['address'],
      },
      fetchSuggestionsCallback,
    );
  });

  const onChange = (e) => {
    if (onInputChange) {
      onInputChange(e);
    }
    const text = e.target.value;

    setInputValue(text);
    setDidSelectAddress(false);

    if (text.length > 0) {
      fetchSuggestions(text);
      handleAddressChange(null);
    } else {
      setSuggestions([]);
      handleAddressChange(null);
      clearAllAddressFields?.();
    }
  };

  const handleClick = useCallback(
    (event) => {
      if (
        !didSelectAddress &&
        !event.target.className.includes('m-google-autocomplete-input')
      ) {
        handleAddressChange(null);
        setSuggestions([]);
      }
    },
    [didSelectAddress, handleAddressChange],
  );

  const getAddressComponentByPlace = ({ address_components }) => {
    const components: any = {};

    address_components.forEach((addressComponent) => {
      const { types, short_name, long_name } = addressComponent;

      types.forEach((type) => {
        if (type === 'locality') {
          components[type] = long_name;
        } else {
          components[type] = short_name;
        }
      });
    });

    return components;
  };

  const placeDetailsCallback = (place) => {
    if (!place) return;
    const addressComponents = getAddressComponentByPlace(place);

    setDidSelectAddress(true);

    if (streetNumber === undefined) {
      addressComponents.formatted_address = place.formatted_address.split(
        ', ',
      )[0];
    }

    handleAddressChange(addressComponents, streetNumber);

    if (addressComponents?.street_number) {
      setInputValue(
        `${addressComponents?.street_number} ${addressComponents?.route}`,
      );
    } else {
      let newInputVal = addressComponents?.route;

      // check to see if streetNumber is actually a number before prepending it
      newInputVal = Number.isNaN(parseInt(streetNumber, 10))
        ? addressComponents.formatted_address
        : `${streetNumber} ${addressComponents?.route}`;

      setInputValue(newInputVal);
    }
  };

  const handleSelect = (suggestion) => {
    if (suggestion) {
      setSuggestions([]);
      streetNumber = suggestion.terms?.[0]?.value;
      placesService.current.getDetails(
        { placeId: suggestion.place_id },
        placeDetailsCallback,
      );
    }
  };

  const handleEnterOrTab = () => {
    if (activeSuggestion !== null) {
      handleSelect(suggestions[activeSuggestion]);
    }
  };

  const changeActiveSuggestion = (direction) => {
    if (suggestions.length === 0) return;

    switch (direction) {
      case 1:
        if (activeSuggestion < suggestions.length - 1) {
          setActiveSuggestion(activeSuggestion + 1);
        }
        break;
      case -1:
        if (activeSuggestion > 0) setActiveSuggestion(activeSuggestion - 1);
        break;
      default:
    }
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case 'Backspace':
        clearCityStateZipFields?.();
        break;
      case 'Enter':
        event.preventDefault();
        handleEnterOrTab();
        break;
      case 'Tab':
        handleEnterOrTab();
        break;
      case 'ArrowDown':
        changeActiveSuggestion(1);
        break;
      case 'ArrowUp':
        changeActiveSuggestion(-1);
        break;
      default:
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClick);

    return function cleanup() {
      document.removeEventListener('click', handleClick);
    };
  }, [didSelectAddress, handleClick]);

  useEffect(() => {
    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }

    if (!placesService.current && window.google) {
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement('div'),
      );
    }
  }, [window.google]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <div
      className="m-google-autocomplete-input"
      data-testid={`${dataTestId}-AddressInput`}
    >
      <TextField
        data-testid={`${dataTestId}-TextField`}
        inputProps={{
          'data-testid': 'NextivaLocationJS-GoogleAutoCompleteInput',
          ref: addressRef,
        }}
        className="m-google-autocomplete-input-location-search-input"
        // label={<AddressTooltip label={label} />}
        label={label}
        disabled={disabled}
        fullWidth
        type="search"
        placeholder="Enter street address"
        value={inputValue}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        error={error}
      />
      <div
        className={clsx(
          'm-google-autocomplete-input-dropdown-container',
          isModal && 'm-google-autocomplete-floating-container',
        )}
      >
        {suggestions.map((suggestion, index) => {
          const activeClassName =
            activeSuggestion === index
              ? 'm-google-autocomplete-input-suggestion-item--active'
              : '';

          return (
            <div
              id={`m-google-autocomplete-input-suggestion--${index}`}
              key={`${suggestion.id}${index}`}
              onClick={() => handleSelect(suggestion)}
              role="presentation"
              className={`m-google-autocomplete-input-suggestion-item ${activeClassName}`}
              data-testid={`${dataTestId}-AddressInput-Suggestion`}
            >
              {suggestion.description}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GoogleAutoCompleteInput;
